import React, {useState, useEffect, useCallback, useMemo} from 'react'; // 用户资料页面
import {Box, Typography, Avatar, Grid, CircularProgress, Fade, Divider, Button, useTheme, Paper, Card, CardContent} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import {getCurrentUser, getUserReadings, getAllReadingsFromDB, isLoggedIn} from '../services/userReadingService';
import {languages, useLanguage} from '../data/languages';
import PersonIcon from '@mui/icons-material/Person';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const ProfileContainer = styled(Box)(({theme}) => ({ // 主容器样式
  minHeight: '100vh',
  background: '#ffffff', // 纯白色背景
  padding: theme.spacing(4), color: '#000000', display: 'flex', flexDirection: 'column', gap: theme.spacing(4),
  fontFamily: '"Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif'
}));

const ProfileHeader = styled(Box)(({theme}) => ({ // 顶部用户信息区域
  display: 'flex', gap: theme.spacing(3), flexWrap: 'wrap', justifyContent: 'center',
  '@media (max-width: 768px)': {flexDirection: 'column', alignItems: 'center'}
}));

const ProfileSection = styled(Box)(({theme}) => ({ // 直接背景渲染的区域样式
  padding: theme.spacing(3), transition: 'all 0.3s ease',
  flex: '1 1 300px', maxWidth: '400px', minHeight: '200px',
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
}));

const StatCard = styled(Box)(({theme}) => ({ // 统计卡片样式 - 直接背景渲染
  textAlign: 'center', padding: theme.spacing(2), transition: 'all 0.3s ease',
  '&:hover': {opacity: 0.8}
}));


const getBackendUrl = () => ''; // API地址

const Profile = () => {
  const [profile, setProfile] = useState(null); // 用户资料状态
  const [userCard, setUserCard] = useState(null); // 用户个人卡片
  const [loading, setLoading] = useState(true); // 加载状态
  const [lastFetchTime, setLastFetchTime] = useState(0); // 上次请求时间
  const [readingsCount, setReadingsCount] = useState(0); // 阅读次数缓存
  const [dataCache, setDataCache] = useState({}); // 数据缓存
  const currentLang = useLanguage(); // 当前语言
  const config = languages[currentLang]; // 语言配置
  const theme = useTheme();
  const navigate = useNavigate(); // 导航钩子
  const user = getCurrentUser();

  // 缓存键生成
  const getCacheKey = useCallback((type, userId) => `${type}_${userId}`, []);

  // 获取本地readings数据（避免频繁API调用）
  const getLocalReadingsData = useCallback(() => {
    const readings = getUserReadings();
    const sortedReadings = readings.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return {
      count: sortedReadings.length,
      lastReading: sortedReadings.length > 0 ? sortedReadings[0].timestamp : null,
      firstReading: sortedReadings.length > 0 ? sortedReadings[sortedReadings.length - 1].timestamp : null, // earliest reading
      readings: sortedReadings
    };
  }, []);

  // 优化数据获取，减少重复请求
  const fetchData = useCallback(async () => {
    if (!user) return;
    const now = Date.now();
    const cacheTimeout = 600000; // 10分钟缓存
    const shouldFetchAPI = now - lastFetchTime > cacheTimeout;

    try {
      let apiData = dataCache.profile || null;
      let cardData = dataCache.card || null;

      // 只在需要时获取API数据
      if (shouldFetchAPI) {
        const [profileResponse, cardResponse] = await Promise.allSettled([
          fetch(`${getBackendUrl()}/login/profile?user_id=${user.id}`).then(r => r.json()),
          fetch(`${getBackendUrl()}/user/card?user_id=${user.id}`).then(r => r.json())
        ]);

        if (profileResponse.status === 'fulfilled') {
          apiData = profileResponse.value;
          setDataCache(prev => ({...prev, profile: apiData}));
        }

        if (cardResponse.status === 'fulfilled' && cardResponse.value.success) {
          cardData = cardResponse.value.card;
          setUserCard(cardData);
          setDataCache(prev => ({...prev, card: cardData}));
        }

        setLastFetchTime(now);
      } else {
        // 使用缓存数据
        cardData = userCard;
      }

      // 使用本地readings数据，避免频繁API调用
      const readingsData = getLocalReadingsData();
      setReadingsCount(readingsData.count);

      // 合并数据
      const profileData = {
        name: user.name || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        joined_at: apiData?.success ? apiData.profile?.joined_at : (profile?.joined_at || new Date().toISOString()),
        total_readings: readingsData.count,
        last_reading: readingsData.lastReading,
        login_count: apiData?.success ? apiData.profile?.login_count || 1 : (profile?.login_count || 1),
        first_login: readingsData.firstReading || (apiData?.success ? apiData.profile?.first_login : (profile?.first_login || new Date().toISOString())), // 使用实际第一次阅读时间
        question_count: readingsData.count
      };

      setProfile(profileData);
    } catch (error) {
      console.error('获取数据失败:', error);
      // 使用本地数据兜底
      const readingsData = getLocalReadingsData();
      if (!profile) {
        setProfile({
          name: user.name || user.email?.split('@')[0] || 'User',
          email: user.email || '',
          joined_at: new Date().toISOString(),
          total_readings: readingsData.count,
          last_reading: readingsData.lastReading,
          login_count: 1,
          first_login: readingsData.firstReading || new Date().toISOString(),
          question_count: readingsData.count
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user, lastFetchTime, dataCache, profile, userCard, getLocalReadingsData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 日期格式化函数优化
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return config.profile.never;
    return new Date(dateStr).toLocaleDateString(currentLang === 'zh' ? 'zh-CN' : 'en-US');
  }, [currentLang, config.profile.never]);

  // 卡片图片URL生成优化
  const getCardImageUrl = useMemo(() => {
    const backendUrl = getBackendUrl();
    return (cardName) => cardName ? `${backendUrl}/cards/${cardName}` : '/card_back.jpg';
  }, []);

  if (!user) return ( // 未登录状态
    <ProfileContainer><Typography color="text.primary">{config.profile.notLoggedIn}</Typography></ProfileContainer>
  );

  if (loading) return ( // 加载状态
    <ProfileContainer><CircularProgress sx={{color: 'primary.main'}}/></ProfileContainer>
  );

  return (
    <Fade in timeout={1000}>
      <ProfileContainer>
        {/* 重新设计的顶部区域：用户信息和守护卡片 */}
        <ProfileHeader>
          {/* 用户信息区域 */}
          <ProfileSection>
            <Avatar sx={{width: 80, height: 80, mb: 2, bgcolor: '#000000', color: '#ffffff', fontSize: '2rem'}}>
              {user.imageUrl ? <img src={user.imageUrl} alt={profile.name} style={{width: '100%', borderRadius: '50%'}}/> : <PersonIcon fontSize="large"/>}
            </Avatar>
            <Typography variant="h5" sx={{fontWeight: 700, mb: 1, color: '#000000'}}>
              {profile.name}
            </Typography>
            <Typography variant="body2" sx={{mb: 2, color: '#666666'}}>
              {profile.email}
            </Typography>
            <Typography variant="caption" sx={{fontSize: '0.85rem', color: '#666666'}}>
              {config.profile.memberSince} {formatDate(profile.joined_at)}
            </Typography>
          </ProfileSection>

          {/* 守护卡片区域 */}
          <ProfileSection>
            {userCard ? (
              <>
                <Typography variant="h6" sx={{color: '#000000', mb: 3, fontWeight: 600}}>
                  {currentLang === 'zh' ? '您的守护卡' : 'Your Guardian Card'}
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                  <Box
                    component="img"
                    src={getCardImageUrl(userCard.image)}
                    alt={userCard.name}
                    sx={{
                      width: 100,
                      height: 160,
                      borderRadius: 2,
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                      mb: 2,
                      objectFit: 'cover'
                    }}
                  />
                  <Typography variant="body1" sx={{fontWeight: 600, color: '#000000', mb: 1}}>
                    {userCard.name}
                  </Typography>
                  <Typography variant="caption" sx={{maxWidth: 280, textAlign: 'center', lineHeight: 1.4, fontSize: '0.85rem', color: '#666666'}}>
                    {userCard.description}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{color: '#000000', mb: 2, fontWeight: 600}}>
                  {currentLang === 'zh' ? '欢迎来到AITAROT' : 'Welcome to AITAROT'}
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1}}>
                  <Typography variant="body2" sx={{mb: 3, lineHeight: 1.4, fontSize: '0.9rem', color: '#666666'}}>
                    {currentLang === 'zh' ? '完成第一次解读后，您将获得专属的塔罗卡片' : 'Complete your first reading to receive your tarot card'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/character')}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#ffffff',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6b4190 100%)'
                      }
                    }}
                  >
                    {currentLang === 'zh' ? '开始解读' : 'Start Reading'}
                  </Button>
                </Box>
              </>
            )}
          </ProfileSection>
        </ProfileHeader>

        {/* 统计数据区域 */}
        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
          <Grid container spacing={3} sx={{maxWidth: '1200px', width: '100%'}}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <QuestionAnswerIcon sx={{fontSize: 32, color: '#000000', mb: 1.5}}/>
                <Typography variant="h4" sx={{fontWeight: 700, color: '#000000', mb: 0.5}}>
                  {profile.total_readings}
                </Typography>
                <Typography variant="body2" sx={{color: '#666666'}}>
                  {config.profile.totalReadings}
                </Typography>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <TrendingUpIcon sx={{fontSize: 32, color: '#000000', mb: 1.5}}/>
                <Typography variant="h4" sx={{fontWeight: 700, color: '#000000', mb: 0.5}}>
                  {profile.login_count}
                </Typography>
                <Typography variant="body2" sx={{color: '#666666'}}>
                  {config.profile.totalLogins}
                </Typography>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CalendarTodayIcon sx={{fontSize: 32, color: '#000000', mb: 1.5}}/>
                <Typography variant="h4" sx={{fontWeight: 700, color: '#000000', mb: 0.5}}>
                  {formatDate(profile.first_login)}
                </Typography>
                <Typography variant="body2" sx={{color: '#666666'}}>
                  {config.profile.firstVisit}
                </Typography>
              </StatCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <AccessTimeIcon sx={{fontSize: 32, color: '#000000', mb: 1.5}}/>
                <Typography variant="h4" sx={{fontWeight: 700, color: '#000000', mb: 0.5}}>
                  {formatDate(profile.last_reading)}
                </Typography>
                <Typography variant="body2" sx={{color: '#666666'}}>
                  {config.profile.lastReading}
                </Typography>
              </StatCard>
            </Grid>
          </Grid>
        </Box>
      </ProfileContainer>
    </Fade>
  );
};

export default Profile;
